"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ChevronRight, ZoomIn } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { getSchoolFacilityList } from "@/store/controller/schoolFacility.controller";
import { ImageLightbox } from "./imageLightbox";

export function FasilitasSection() {
  const dispatch = useAppDispatch();
  const facilities = useAppSelector((state) => state.schoolFacility.list);
  const [hovered, setHovered] = useState<number | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    dispatch(getSchoolFacilityList({}));
  }, [dispatch]);

  return (
    <section id="fasilitas" className="py-20 bg-white">
      {lightbox !== null && (
        <ImageLightbox
          src={facilities[lightbox].image}
          alt={facilities[lightbox].description}
          title={facilities[lightbox].title}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((previousIndex) => (previousIndex! - 1 + facilities.length) % facilities.length)}
          onNext={() => setLightbox((previousIndex) => (previousIndex! + 1) % facilities.length)}
          hasPrev={lightbox > 0}
          hasNext={lightbox < facilities.length - 1}
        />
      )}
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-14">
          <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest mb-3">
            Infrastruktur
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">
            Galeri <span className="text-green-700">Fasilitas</span>
          </h2>
          <div className="w-14 h-1 bg-yellow-400 rounded mx-auto mb-4" />
          <p className="text-gray-500 max-w-xl mx-auto">
            Fasilitas lengkap dan modern untuk mendukung proses belajar mengajar yang optimal.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {facilities.map((facility, facilityIndex) => (
            <div
              key={facility.id}
              className={`relative rounded-2xl overflow-hidden group cursor-pointer ${facility.span === "col-span-2" ? "sm:col-span-2" : ""} ${
                facilityIndex === 0 ? "sm:h-72" : facilityIndex === 3 ? "sm:h-56" : "sm:h-64"
              }`}
              onMouseEnter={() => setHovered(facilityIndex)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => setLightbox(facilityIndex)}
              style={{ backgroundColor: "#e5e7eb" }}
            >
              <img
                src={facility.image}
                alt={facility.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div
                className="absolute inset-0 transition-opacity duration-300"
                style={{
                  background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)",
                  opacity: hovered === facilityIndex ? 1 : 0.7,
                }}
              />
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-white font-bold text-lg leading-tight">{facility.title}</h3>
                <p className="text-white/70 text-sm mt-0.5">{facility.description}</p>
              </div>
              <div
                className={`absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-200 ${
                  hovered === facilityIndex ? "opacity-100 scale-100" : "opacity-0 scale-75"
                }`}
              >
                <ZoomIn size={16} />
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            href="/fasilitas"
            className="inline-flex items-center gap-2 text-green-700 border border-green-700 hover:bg-green-700 hover:text-white font-semibold px-6 py-3 rounded-xl transition-all text-sm"
          >
            Lihat Semua Fasilitas <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
